interface TelegramWebApp {
  initData: string;
  initDataUnsafe?: { user?: { id: number } };
  colorScheme?: 'light' | 'dark';
  themeParams?: Record<string, string>;
  ready: () => void;
  expand: () => void;
  setHeaderColor?: (color: string) => void;
  setBackgroundColor?: (color: string) => void;
  onEvent?: (event: string, handler: () => void) => void;
  BackButton?: {
    show: () => void;
    hide: () => void;
    onClick: (handler: () => void) => void;
    offClick: (handler: () => void) => void;
  };
  HapticFeedback?: {
    impactOccurred: (style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft') => void;
    notificationOccurred: (type: 'error' | 'success' | 'warning') => void;
  };
}

declare global {
  interface Window {
    Telegram?: { WebApp?: TelegramWebApp };
  }
}

export function getWebApp(): TelegramWebApp | null {
  return window.Telegram?.WebApp ?? null;
}

const THEME_VARIABLES: Record<string, string> = {
  bg_color: '--bg',
  secondary_bg_color: '--surface',
  text_color: '--text',
  hint_color: '--muted',
  link_color: '--accent',
  button_color: '--accent',
  button_text_color: '--accent-text',
  section_separator_color: '--border',
  destructive_text_color: '--danger',
};

function applyTheme(): void {
  const webApp = getWebApp();
  if (!webApp) return;

  const root = document.documentElement;
  const params = webApp.themeParams ?? {};

  for (const [key, variable] of Object.entries(THEME_VARIABLES)) {
    const value = params[key];
    if (value) root.style.setProperty(variable, value);
  }

  if (webApp.colorScheme) root.dataset.theme = webApp.colorScheme;
}

export function initTelegram(): void {
  const webApp = getWebApp();
  if (!webApp) {
    document.documentElement.dataset.theme =
      window.matchMedia?.('(prefers-color-scheme: light)').matches ? 'light' : 'dark';
    return;
  }

  webApp.ready();
  webApp.expand();
  applyTheme();

  const background = webApp.themeParams?.bg_color;
  if (background) {
    webApp.setHeaderColor?.(background);
    webApp.setBackgroundColor?.(background);
  }

  webApp.onEvent?.('themeChanged', applyTheme);
}

let backHandler: (() => void) | null = null;

export function setBackButton(visible: boolean, handler?: () => void): void {
  const backButton = getWebApp()?.BackButton;
  if (!backButton) return;

  if (backHandler) {
    backButton.offClick(backHandler);
    backHandler = null;
  }

  if (visible && handler) {
    backHandler = handler;
    backButton.onClick(handler);
    backButton.show();
  } else {
    backButton.hide();
  }
}

export function haptic(style: 'light' | 'medium' | 'heavy' = 'light'): void {
  getWebApp()?.HapticFeedback?.impactOccurred(style);
}
